import { c as _c } from "react/compiler-runtime";
// Top-N leaderboard, borrowed from the seller panel's analytics ("Топ 5 товаров
// с высокими возвратами", "Топ 5 товаров с задержкой выполнения").
//
// The idea worth stealing is not the ranking — it's that each row carries the
// REASON beside the number. A returns count tells you something is wrong; the
// dominant reason tells you what to do about it.
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import type { RankedItem, RankedList as RankedListData, StatTone } from "@/api";
import { Card, CardContent, CardHeader, CardTitle } from "@osmix/ui/card";
import { formatNumber } from "@osmix/formatters";
import { cn } from "@/lib/utils";
const TONE_TEXT: Record<StatTone, string> = {
  default: "",
  warning: "text-amber-600 dark:text-amber-500",
  danger: "text-destructive"
};
function Row(t0) {
  const $ = _c(31);
  const {
    item,
    rank,
    unit,
    reasonPrefix
  } = t0;
  const {
    t
  } = useTranslation();
  let t1;
  if ($[0] !== rank) {
    t1 = <span className="w-5 shrink-0 text-lg font-semibold tabular-nums text-muted-foreground/50">{rank}</span>;
    $[0] = rank;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== item.label) {
    t2 = <span className="block truncate text-sm font-medium">{item.label}</span>;
    $[2] = item.label;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== item.sublabel) {
    t3 = item.sublabel && <span className="block truncate text-xs text-muted-foreground">{item.sublabel}</span>;
    $[4] = item.sublabel;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t2 || $[7] !== t3) {
    t4 = <span className="min-w-0 flex-1">{t2}{t3}</span>;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== item.reason || $[10] !== reasonPrefix || $[11] !== t) {
    t5 = item.reason && <span className="hidden shrink-0 rounded-full border px-2 py-0.5 text-xs text-muted-foreground sm:inline">{t(`${reasonPrefix}.${item.reason}`, item.reason)}</span>;
    $[9] = item.reason;
    $[10] = reasonPrefix;
    $[11] = t;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const t6 = TONE_TEXT[item.tone ?? "default"];
  let t7;
  if ($[13] !== t6) {
    t7 = cn("shrink-0 text-right text-sm font-semibold tabular-nums", t6);
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== item.value) {
    t8 = formatNumber(item.value);
    $[15] = item.value;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== unit) {
    t9 = unit && <span className="ml-1 font-normal text-muted-foreground">{unit}</span>;
    $[17] = unit;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== t7 || $[20] !== t8 || $[21] !== t9) {
    t10 = <span className={t7}>{t8}{t9}</span>;
    $[19] = t7;
    $[20] = t8;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== t1 || $[24] !== t10 || $[25] !== t4 || $[26] !== t5) {
    t11 = <>{t1}{t4}{t5}{t10}</>;
    $[23] = t1;
    $[24] = t10;
    $[25] = t4;
    $[26] = t5;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  const body = t11;
  let t12;
  if ($[28] !== body || $[29] !== item.href) {
    t12 = item.href ? <Link to={item.href} className={cn("flex items-center gap-3 border-b py-2 last:border-0", "-mx-2 rounded px-2 transition-colors hover:bg-muted/50")}>{body}</Link> : <div className="flex items-center gap-3 border-b py-2 last:border-0">{body}</div>;
    $[28] = body;
    $[29] = item.href;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  return t12;
}
export function RankedList(t0) {
  const $ = _c(17);
  const {
    list,
    title,
    reasonPrefix
  } = t0;
  const {
    t
  } = useTranslation();
  let t1;
  if ($[0] !== list.unitKey || $[1] !== t) {
    t1 = list.unitKey ? t(list.unitKey) : undefined;
    $[0] = list.unitKey;
    $[1] = t;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const unit = t1;
  let t2;
  if ($[3] !== title) {
    t2 = <CardHeader className="pb-2"><CardTitle className="text-base">{title}</CardTitle></CardHeader>;
    $[3] = title;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== list.items || $[6] !== reasonPrefix || $[7] !== unit) {
    let t4;
    if ($[9] !== reasonPrefix || $[10] !== unit) {
      t4 = (item, i) => <Row key={item.key} item={item} rank={i + 1} unit={unit} reasonPrefix={reasonPrefix} />;
      $[9] = reasonPrefix;
      $[10] = unit;
      $[11] = t4;
    } else {
      t4 = $[11];
    }
    t3 = list.items.map(t4);
    $[5] = list.items;
    $[6] = reasonPrefix;
    $[7] = unit;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[12] !== t3) {
    t4 = <CardContent className="flex flex-col">{t3}</CardContent>;
    $[12] = t3;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== t2 || $[15] !== t4) {
    t5 = <Card>{t2}{t4}</Card>;
    $[14] = t2;
    $[15] = t4;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  return t5;
}